import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import type { NavigateFunction } from 'react-router-dom';
import { useLazyGetTemplateFunctionByIdQuery } from 'store/services/helios/templateFunctions';
import { useRunHeliosHegFunctionMutation, useRunHeliosHegWorkflowFunctionMutation } from 'store/services/helios/runFunctions';
import { useModals } from 'utils/hooks';
import { getHeliosCreatedRecordUrl } from 'utils/helios/navigation';
import { showHeliosFunctionResultError } from 'utils/helios/runFunctionResult';
import type { HeliosRecordFunction } from 'types/dataHelios';
import type { runHeliosHegFunctionReq, runHeliosHegFunctionRes, runHeliosHegWorkflowFunctionReq } from 'types/helios/runFunctions';
import type { FunctionModalConfirmData } from 'components/user/DummyHelios/Functions/modals/FunctionModal';
import type { HeliosFunctionContext } from 'utils/helios/buildHeliosFunctionPayload';

export type { HeliosFunctionContext };

type UseHeliosFunctionRunnerOptions = {
  templateViewId: string;
  moduleId?: string;
  navigate?: NavigateFunction;
  getNavigateState?: () => Record<string, unknown> | undefined;
  buildPayload: (args: {
    context: HeliosFunctionContext;
    request?: FunctionModalConfirmData['request'];
  }) => runHeliosHegFunctionReq | runHeliosHegWorkflowFunctionReq;
  onSuccess?: () => void | Promise<void>;
  clearFunctionRefDelay?: number;
};

export const useHeliosFunctionRunner = ({
  templateViewId,
  moduleId,
  navigate,
  getNavigateState,
  buildPayload,
  onSuccess,
  clearFunctionRefDelay = 0,
}: UseHeliosFunctionRunnerOptions) => {
  const { t } = useTranslation();

  const functionRef = useRef<HeliosFunctionContext | null>(null);

  const [getTemplateFunctionById] = useLazyGetTemplateFunctionByIdQuery();
  const [runHeliosHegFunction, { isLoading: isFunctionRunning }] = useRunHeliosHegFunctionMutation();
  const [runHeliosHegWorkflowFunction, { isLoading: isWorkflowFunctionRunning }] = useRunHeliosHegWorkflowFunctionMutation();

  const [functionModals, openFunctionModal, closeFunctionModal] = useModals({
    functions: false,
    confirmFunction: false,
  });

  const clearFunctionRef = useCallback(() => {
    if (clearFunctionRefDelay > 0) {
      setTimeout(() => {
        functionRef.current = null;
      }, clearFunctionRefDelay);
    } else {
      functionRef.current = null;
    }
  }, [clearFunctionRefDelay]);

  const runFunction = useCallback(
    async (functionDataWithCallback?: FunctionModalConfirmData) => {
      const { onSuccess: onModalSuccess, request: functionData } = functionDataWithCallback || {};

      if (!functionRef.current?.function) return;

      try {
        const isWorkflow = functionRef.current.function.functionType === 'HegWorkflow';
        const payload = buildPayload({
          context: functionRef.current,
          request: functionData,
        });

        const executeFunction = isWorkflow ? runHeliosHegWorkflowFunction : runHeliosHegFunction;
        const res = await executeFunction(payload as runHeliosHegFunctionReq & runHeliosHegWorkflowFunctionReq);

        if (!('error' in res)) {
          const resultData = 'data' in res ? (res.data as runHeliosHegFunctionRes | undefined) : undefined;

          if (showHeliosFunctionResultError(resultData, t)) {
            return;
          }

          closeFunctionModal('functions')();
          closeFunctionModal('confirmFunction')();
          toast.success(t('helios.templates.functions.triggerFunctionModal.success'));
          onModalSuccess?.();
          await Promise.resolve(onSuccess?.());
          clearFunctionRef();

          const createdRecordUrl = getHeliosCreatedRecordUrl(resultData, moduleId, templateViewId, isWorkflow);
          if (createdRecordUrl && navigate) {
            const navigateState = getNavigateState?.();
            navigate(createdRecordUrl, navigateState ? { state: navigateState } : undefined);
          }
        }
      } catch {
        toast.error(t('common.error'));
      }
    },
    [
      buildPayload,
      clearFunctionRef,
      closeFunctionModal,
      getNavigateState,
      moduleId,
      navigate,
      onSuccess,
      runHeliosHegFunction,
      runHeliosHegWorkflowFunction,
      t,
      templateViewId,
    ]
  );

  const handleHeliosFunction = useCallback(
    async ({ func, rowId }: { func: HeliosRecordFunction; rowId?: string }) => {
      const isHegWorkFlow = func.functionType === 'HegWorkflow';
      try {
        const res = await getTemplateFunctionById({ id: func.id });
        if ('error' in res) {
          throw new Error('Failed to get function data');
        }
        if (
          (func.requiresConfirmation && isHegWorkFlow && func.workflowCommentType === 'None') ||
          (func.requiresConfirmation && !isHegWorkFlow && res.data?.parameters.length === 0)
        ) {
          functionRef.current = { function: func };
          openFunctionModal('confirmFunction')();
          return;
        }
        if ((isHegWorkFlow && func.workflowCommentType !== 'None') || (!isHegWorkFlow && (res.data?.parameters ?? []).length > 0)) {
          functionRef.current = {
            function: func,
            functionData: res.data,
            rowId,
          };
          openFunctionModal('functions')();
          return;
        }
        functionRef.current = {
          function: func,
          functionData: res.data,
          rowId,
        };
        runFunction();
      } catch {
        toast.error(t('helios.templates.functions.triggerFunctionModal.error'));
      }
    },
    [getTemplateFunctionById, openFunctionModal, runFunction, t]
  );

  return {
    functionRef,
    runFunction,
    handleHeliosFunction,
    isFunctionRunning: isFunctionRunning || isWorkflowFunctionRunning,
    functionModals,
    openFunctionModal,
    closeFunctionModal,
  };
};
